import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { classnames } from 'helpers/utils';
import { ContentThumbnail } from 'common/content-thumbnail';
import { AlbumUploadEntryProps } from 'context/self-recording-context/context-reducer';
import { AlbumContentSerialized } from 'networking/types/album-detail';
import { formatContentDate } from '../../single-album-utils';
import { MemoryCardMenu } from './components/memory-card-menu';
import { UploadingCard } from './components/uploading-card';
import styles from './memory-grid.module.scss';

interface MemoryGridProps {
  contents: AlbumContentSerialized[];
  onSelect: (content: AlbumContentSerialized) => void;
  onContentUpdated: (
    contentId: number,
    partial: Partial<AlbumContentSerialized>,
  ) => void;
  onContentRemoved: (contentId: number) => void;
  // The vault's hidden default album ("Remove from album" moves memories
  // there); absent on that album itself.
  unsortedAlbumId?: number;
  // In-progress uploads shown as dimmed placeholder cards until they finish.
  uploads?: AlbumUploadEntryProps[];
  // Awaited by the placeholder before it removes itself, so the refetched real
  // card lands in the same render (seamless in-place swap).
  onUploadSuccess?: () => void | Promise<void>;
  // Where the uploading placeholder(s) belong in the current sort order. A new
  // memory is the newest item, so it goes first for "newest" and last (default)
  // for "oldest" — this keeps the finished thumbnail in the same spot the
  // loading card occupied instead of jumping across the grid.
  uploadsAtStart?: boolean;
}

interface MemoryGridCardProps {
  content: AlbumContentSerialized;
  index: number;
  isNew: boolean;
  onSelect: (content: AlbumContentSerialized) => void;
  onContentUpdated: (
    contentId: number,
    partial: Partial<AlbumContentSerialized>,
  ) => void;
  onContentRemoved: (contentId: number) => void;
  unsortedAlbumId?: number;
}

// Split out of the grid so each card can measure its own title: only a title
// that overflows one line earns the two-line hover treatment, and CSS can't
// detect overflow.
const MemoryGridCard = ({
  content,
  index,
  isNew,
  onSelect,
  onContentUpdated,
  onContentRemoved,
  unsortedAlbumId,
}: MemoryGridCardProps) => {
  const titleRef = useRef<HTMLSpanElement>(null);
  const isHoveredRef = useRef(false);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el) return undefined;

    // Skip while hovered: the title is wrapped to two lines then, so it would
    // measure as fitting, drop the class, snap back to one line and re-measure
    // as overflowing — a flicker loop.
    const measure = () => {
      if (isHoveredRef.current) return;
      setIsTitleTruncated(el.scrollWidth > el.clientWidth);
    };

    measure();
    // Re-measure on resize and once the webfont swaps in, either of which
    // changes whether the title fits.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [content.name]);

  return (
    <div
      className={classnames(styles.card, 'gs-stagger', {
        [styles.cardNew]: isNew,
        [styles.cardTitleTruncated]: isTitleTruncated,
      })}
      style={{ '--stagger': index } as React.CSSProperties}
      onMouseEnter={() => {
        isHoveredRef.current = true;
      }}
      onMouseLeave={() => {
        isHoveredRef.current = false;
      }}
    >
      <button
        type="button"
        className={styles.cardSelect}
        onClick={() => onSelect(content)}
      >
        <span className={styles.thumb}>
          <ContentThumbnail content={content} showVideoOverlay />
        </span>
        <span className={styles.cardTitleBlock}>
          <span className={styles.cardTitle} ref={titleRef}>
            {content.name || 'Untitled'}
          </span>
          <span className={styles.cardDate}>{formatContentDate(content)}</span>
        </span>
      </button>
      <MemoryCardMenu
        content={content}
        onContentUpdated={onContentUpdated}
        onContentRemoved={onContentRemoved}
        unsortedAlbumId={unsortedAlbumId}
      />
    </div>
  );
};

export const MemoryGrid = ({
  contents,
  onSelect,
  onContentUpdated,
  onContentRemoved,
  unsortedAlbumId,
  uploads = [],
  onUploadSuccess,
  uploadsAtStart = false,
}: MemoryGridProps) => {
  // Contents seen on previous renders. A content id that appears later (e.g.
  // the refetched entry replacing its uploading placeholder) gets a fade-in so
  // the swap reads as the placeholder "loading into" the real card.
  const knownIdsRef = useRef<Set<number> | null>(null);
  const isFreshContent = (id: number) =>
    knownIdsRef.current !== null && !knownIdsRef.current.has(id);
  useEffect(() => {
    if (knownIdsRef.current === null) {
      knownIdsRef.current = new Set(contents.map((content) => content.id));
    } else {
      contents.forEach((content) => knownIdsRef.current!.add(content.id));
    }
  }, [contents]);
  if (contents.length === 0 && uploads.length === 0) {
    return (
      <div className={styles.emptyState}>
        <span className={styles.emptyTitle}>No memories yet</span>
        <span className={styles.emptyText}>
          Use the New button above to add your first entry.
        </span>
      </div>
    );
  }

  const uploadCards = uploads.map((upload) => (
    <UploadingCard
      key={upload.uploadId}
      upload={upload}
      onSuccess={() => onUploadSuccess?.()}
    />
  ));

  return (
    <div className={styles.grid}>
      {uploadsAtStart && uploadCards}
      {contents.map((content, index) => (
        <MemoryGridCard
          key={content.id}
          content={content}
          index={index}
          isNew={isFreshContent(content.id)}
          onSelect={onSelect}
          onContentUpdated={onContentUpdated}
          onContentRemoved={onContentRemoved}
          unsortedAlbumId={unsortedAlbumId}
        />
      ))}
      {!uploadsAtStart && uploadCards}
    </div>
  );
};
